import {
  DESTINOS_DE_VIAJE,
  ESTADOS_DE_VIAJE,
  ETIQUETA_DESTINO_VIAJE,
  ETIQUETA_DESTINO_VIAJE_EN,
  ETIQUETA_ESTADO_VIAJE,
  ETIQUETA_ESTADO_VIAJE_EN,
  type Viaje,
} from '@cnc/contracts';
import {useEffect, useMemo, useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {rutaExcelDeViajes, useViajes} from '../api/transporte';
import {CargaDeViajes} from '../componentes/carga-de-viajes';
import {AYUDA_FILTROS_VIAJES} from '../componentes/ayuda-inventario';
import {ChipsDeTotales} from '../componentes/chips-de-totales';
import {COLUMNAS_PERSONALES_DE_VIAJE, DEFINICION_VIAJES} from '../componentes/columnas-viajes';
import {EditorDeViaje} from '../componentes/editor-de-viaje';
import {EncabezadoInventario} from '../componentes/encabezado-inventario';
import {EntradaABodega} from '../componentes/entrada-a-bodega';
import {MenuDeHerramientas, OPCION_DE_MENU} from '../componentes/menu-de-herramientas';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {Paginacion} from '../componentes/paginacion';
import {SelectorDeColumnas} from '../componentes/selector-de-columnas';
import {SelectorMultiple} from '../componentes/selector-multiple';
import {usePermisos} from '../componentes/sesion-contexto';
import {Tabla} from '../componentes/tabla';
import {useColumnas} from '../componentes/usar-columnas';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {et, tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';

// Viajes de camion de cada recoleccion: de la bodega de Quintana a Cartagena
// o Barrancabermeja, en contenedor terrestre. Toda recoleccion del
// Consolidated tiene al menos uno (decision 0016). Administradores y editores
// editan fila por fila o con el Excel "para editar"; conductor, cedula y
// placa solo los ve y cambia un administrador. Mismo formato que el
// Consolidado (Daniel, 13/10/2026): Herramientas, filtros, totales, tabla
// comun con columnas elegibles y paginas. Los filtros viven en la direccion.
export function PaginaRecolecciones() {
  const [parametros, setParametros] = useSearchParams();
  const viajes = useViajes(parametros);
  const permisos = usePermisos();
  const [editando, setEditando] = useState<number | 'nuevo' | null>(null);
  const [cargando, setCargando] = useState(false);
  // Panel de fecha de ingreso a bodega: null cerrado; '' sin recoleccion elegida.
  const [ingreso, setIngreso] = useState<string | null>(null);
  const [columnas, cambiarColumnas] = useColumnas(DEFINICION_VIAJES);
  const x = viajes.data;
  const personales = x?.datosPersonalesVisibles ?? false;
  // Solo las columnas que esta persona puede ver.
  const definicion = useMemo(
    () => ({
      ...DEFINICION_VIAJES,
      columnas: DEFINICION_VIAJES.columnas.filter(
        c => (personales || !COLUMNAS_PERSONALES_DE_VIAJE.includes(c.id)) && (permisos.editar || c.id !== 'acciones'),
      ),
    }),
    [personales, permisos.editar],
  );
  const visibles = columnas.filter(id => definicion.columnas.some(c => c.id === id));
  const cambiar = (clave: string, valor: string) => {
    const nuevos = new URLSearchParams(window.location.search);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    if (clave !== 'pagina') nuevos.delete('pagina');
    setParametros(nuevos);
  };
  const orden = parametros.get('orden') ?? '';
  const direccion = parametros.get('direccion') === 'desc' ? 'desc' : 'asc';
  const ordenar = (columna: string) => {
    const nuevos = new URLSearchParams(window.location.search);
    nuevos.set('direccion', orden === columna && direccion === 'asc' ? 'desc' : 'asc');
    nuevos.set('orden', columna);
    setParametros(nuevos, {replace: true});
  };
  const enEdicion = x && typeof editando === 'number' ? x.elementos.find(v => v.id === editando) : undefined;

  return (
    <section className="space-y-4">
      <EncabezadoInventario
        descripcion={tr(
          'Viajes de camión de cada recolección, de la bodega de Quintana a Cartagena o Barrancabermeja, en contenedor terrestre.',
          'Truck trips of each pickup, from the Quintana warehouse to Cartagena or Barrancabermeja, in an inland container.',
        )}
        herramientas={
          <MenuDeHerramientas>
            {cerrar => (
              <>
                <a href={rutaExcelDeViajes(parametros)} onClick={cerrar} className={OPCION_DE_MENU}>
                  {tr('⬇ Excel para editar', '⬇ Excel to edit')}
                </a>
                {permisos.editar && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setCargando(true);
                        cerrar();
                      }}
                      className={OPCION_DE_MENU}
                    >
                      {tr('⬆ Cargar cambios', '⬆ Upload changes')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditando('nuevo');
                        cerrar();
                      }}
                      className={OPCION_DE_MENU}
                    >
                      {tr('+ Agregar viaje', '+ Add trip')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIngreso('');
                        cerrar();
                      }}
                      className={OPCION_DE_MENU}
                    >
                      {tr('📅 Ingreso a bodega', '📅 Warehouse entry')}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    void viajes.refetch();
                    cerrar();
                  }}
                  className={OPCION_DE_MENU}
                >
                  {tr('↻ Actualizar', '↻ Refresh')}
                </button>
                <SelectorDeColumnas
                  definicion={definicion}
                  visibles={visibles}
                  onCambiar={cambiarColumnas}
                  claseDelBoton={OPCION_DE_MENU}
                />
              </>
            )}
          </MenuDeHerramientas>
        }
      />
      {cargando && <CargaDeViajes onCerrar={() => setCargando(false)} />}
      {ingreso !== null && (
        <EntradaABodega key={ingreso} recoleccionInicial={ingreso} onCerrar={() => setIngreso(null)} />
      )}
      {x && editando === 'nuevo' && (
        <div className="rounded-lg border border-linea bg-white">
          <EditorDeViaje
            viaje={null}
            personales={personales}
            transportistas={x.opciones.transportistas}
            onListo={() => setEditando(null)}
          />
        </div>
      )}
      {x && enEdicion && (
        <div className="rounded-lg border border-verde bg-white">
          <p className="px-3 pt-2 text-sm font-medium text-verde-oscuro">
            {tr(`Editar viaje de ${enEdicion.recoleccion ?? ''}`, `Edit trip of ${enEdicion.recoleccion ?? ''}`)}
          </p>
          <EditorDeViaje
            key={enEdicion.id}
            viaje={enEdicion}
            personales={personales}
            transportistas={x.opciones.transportistas}
            onListo={() => setEditando(null)}
          />
        </div>
      )}

      <ZonaAmpliable titulo={tr('Recolecciones', 'Pickups')}>
        <FiltrosDeViajes
          parametros={parametros}
          cambiar={cambiar}
          anios={x?.opciones.anios ?? []}
          transportistas={x?.opciones.transportistas ?? []}
          personales={personales}
          limpiar={() => setParametros(new URLSearchParams())}
        />

        {viajes.isError && <p className="text-sm text-alerta">{viajes.error.message}</p>}
        {x && (
          <ChipsDeTotales
            prueba="totales-viajes"
            datos={[
              [tr('Viajes', 'Trips'), numero(x.totales.viajes)],
              [tr('Toneladas realizadas', 'Tonnes completed'), numero(x.totales.toneladas)],
              [tr('Para revisar', 'To review'), numero(x.totales.paraRevisar)],
            ]}
          />
        )}

        <TablaDeViajes
          filas={x?.elementos ?? []}
          definicion={definicion}
          columnas={visibles}
          orden={orden}
          direccion={direccion}
          onOrdenar={ordenar}
          onReordenar={cambiarColumnas}
          cargando={viajes.isFetching}
          numeracionDesde={((x?.pagina ?? 1) - 1) * (x?.porPagina ?? 50)}
          onEditar={
            permisos.editar
              ? v => {
                  setEditando(editando === v.id ? null : v.id);
                  window.scrollTo({top: 0, behavior: 'smooth'});
                }
              : undefined
          }
          onIngreso={
            permisos.editar
              ? v => {
                  setIngreso(v.recoleccion!);
                  window.scrollTo({top: 0, behavior: 'smooth'});
                }
              : undefined
          }
        />

        <Paginacion
          pagina={x?.pagina ?? 1}
          totalPaginas={x?.totalPaginas ?? 0}
          porPagina={x?.porPagina ?? 50}
          total={x?.total ?? 0}
          unidad={{es: 'viajes', en: 'trips'}}
          onPagina={p => cambiar('pagina', String(p))}
          onPorPagina={n => cambiar('porPagina', String(n))}
        />
      </ZonaAmpliable>
    </section>
  );
}

// La tabla comun (la del Consolidado) con las columnas de los viajes. La
// recoleccion lleva a sus Big Bags en el inventario; "Acciones" edita el viaje
// o pone la fecha de ingreso a bodega de sus Big Bags.
function TablaDeViajes(props: {
  filas: Viaje[];
  definicion: typeof DEFINICION_VIAJES;
  columnas: string[];
  orden: string;
  direccion: 'asc' | 'desc';
  onOrdenar: (orden: string) => void;
  onReordenar: (ids: string[]) => void;
  cargando: boolean;
  numeracionDesde: number;
  onEditar?: (v: Viaje) => void;
  onIngreso?: (v: Viaje) => void;
}) {
  return (
    <div data-prueba="viajes">
      <Tabla
        filas={props.filas}
        definicion={props.definicion}
        columnasVisibles={props.columnas}
        claveDeFila={v => v.id}
        orden={props.orden}
        direccion={props.direccion}
        onOrdenar={props.onOrdenar}
        onReordenar={props.onReordenar}
        cargando={props.cargando}
        numeracionDesde={props.numeracionDesde}
        mensajeVacio={tr('No hay viajes con estos filtros.', 'No trips with these filters.')}
        claseDeFila={v => (v.estado === 'cancelado' ? 'text-apagado' : v.paraRevisar ? 'bg-amber-50' : undefined)}
        celda={(columna, v) => {
          if (columna.id === 'recoleccion' && v.recoleccionEnApp && v.recoleccion)
            return (
              <Link
                to={`/inventario?recoleccion=${encodeURIComponent(v.recoleccion)}`}
                className="font-medium text-verde hover:underline"
              >
                {v.recoleccion}
              </Link>
            );
          if ((columna.id === 'salida' || columna.id === 'descarga') && v.fechasOriginales)
            return (
              <span
                title={tr(
                  `Fechas corregidas al cargar; el archivo decía salida ${fecha(v.fechasOriginales.salida)} y descarga ${fecha(v.fechasOriginales.descarga)}.`,
                  `Dates corrected on load; the file said departure ${fecha(v.fechasOriginales.salida)} and unloading ${fecha(v.fechasOriginales.descarga)}.`,
                )}
              >
                {columna.valor(v)}
              </span>
            );
          if (columna.id === 'acciones')
            return props.onEditar ? (
              <span className="whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => props.onEditar!(v)}
                  aria-label={tr(`Editar viaje de ${v.recoleccion ?? ''}`, `Edit trip of ${v.recoleccion ?? ''}`)}
                  className="rounded-md border border-linea px-2 py-0.5 text-xs hover:bg-papel"
                >
                  {tr('✎ Editar', '✎ Edit')}
                </button>
                {props.onIngreso && v.recoleccionEnApp && v.recoleccion && (
                  <button
                    type="button"
                    onClick={() => props.onIngreso!(v)}
                    title={tr('Fecha de ingreso a bodega de sus Big Bags', 'Warehouse entry date of its Big Bags')}
                    aria-label={tr(`Ingreso a bodega de ${v.recoleccion}`, `Warehouse entry of ${v.recoleccion}`)}
                    className="ml-1 rounded-md border border-linea px-2 py-0.5 text-xs hover:bg-papel"
                  >
                    📅
                  </button>
                )}
              </span>
            ) : (
              ''
            );
          return undefined;
        }}
      />
    </div>
  );
}

// Filtros con el formato del Consolidado: buscar mientras se escribe y
// listas de varios valores (decision 0018).
function FiltrosDeViajes({
  parametros,
  cambiar,
  anios,
  transportistas,
  personales,
  limpiar,
}: {
  parametros: URLSearchParams;
  cambiar: (clave: string, valor: string) => void;
  anios: number[];
  transportistas: string[];
  personales: boolean;
  limpiar: () => void;
}) {
  const buscar = parametros.get('buscar') ?? '';
  const [busqueda, setBusqueda] = useState(buscar);
  useEffect(() => {
    const t = setTimeout(() => {
      if (busqueda.trim() !== buscar) cambiar('buscar', busqueda.trim());
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda]);
  useEffect(() => setBusqueda(buscar), [buscar]);
  const destinos = et(ETIQUETA_DESTINO_VIAJE, ETIQUETA_DESTINO_VIAJE_EN);
  const estados = et(ETIQUETA_ESTADO_VIAJE, ETIQUETA_ESTADO_VIAJE_EN);
  const activos = ['buscar', 'anio', 'transportista', 'destino', 'estado', 'soloParaRevisar'].filter(c =>
    parametros.get(c),
  ).length;
  const lista = (clave: keyof typeof AYUDA_FILTROS_VIAJES, etiqueta: string, opciones: Array<[string, string]>) => (
    <SelectorMultiple
      etiqueta={etiqueta}
      ayuda={AYUDA_FILTROS_VIAJES[clave]}
      valor={parametros.get(clave) ?? undefined}
      opciones={opciones.map(([valor, texto]) => ({valor, etiqueta: texto}))}
      onCambiar={v => cambiar(clave, v ?? '')}
    />
  );
  return (
    <div className="rounded-lg border border-linea bg-white px-3 py-2">
      <div className="grid items-end gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={AYUDA_FILTROS_VIAJES.buscar} dentroDeEtiqueta />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={
              personales
                ? tr('Recolección, contenedor, placa o conductor', 'Pickup, container, plate or driver')
                : tr('Recolección o contenedor', 'Pickup or container')
            }
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        {lista(
          'anio',
          tr('Año', 'Year'),
          anios.map(a => [String(a), String(a)]),
        )}
        {lista(
          'transportista',
          tr('Transportadora', 'Carrier'),
          transportistas.map(t => [t, t]),
        )}
        {lista(
          'destino',
          tr('Destino', 'Destination'),
          DESTINOS_DE_VIAJE.map(d => [d, destinos[d]]),
        )}
        {lista(
          'estado',
          tr('Estado', 'Status'),
          ESTADOS_DE_VIAJE.map(e => [e, estados[e]]),
        )}
        <label className="flex items-center gap-2 pb-1 text-sm">
          <input
            type="checkbox"
            checked={parametros.get('soloParaRevisar') === 'true'}
            onChange={e => cambiar('soloParaRevisar', e.target.checked ? 'true' : '')}
          />
          {tr('Solo para revisar', 'Only to review')}
          <NotaDeAyuda texto={AYUDA_FILTROS_VIAJES.soloParaRevisar} dentroDeEtiqueta />
        </label>
      </div>
      {activos > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs text-apagado">
          <span>
            {tr(
              `${activos} filtro${activos === 1 ? '' : 's'} activo${activos === 1 ? '' : 's'}`,
              `${activos} active filter${activos === 1 ? '' : 's'}`,
            )}
          </span>
          <button type="button" onClick={limpiar} className="text-verde hover:underline">
            {tr('Quitar filtros', 'Clear filters')}
          </button>
        </div>
      )}
    </div>
  );
}
